import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { cloneDeep } from 'lodash-es';
import { MessageService, TreeNode } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TreeSelectModule } from 'primeng/treeselect';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { firstValueFrom } from 'rxjs';
import { afterRender } from '../../../../common/after-render';
import { notifyAndTrackError } from '../../../../common/error';
import { dateToIsoString, isEmpty, isStringEmpty, isStringEqual, parseLocalDate } from '../../../../common/functions';
import { errorMsg, successMsg, warningMsg } from '../../../../common/messages';
import { aggregateValidation, VALIDATION_PREFIX } from '../../../../common/validation';
import { ProcessingComponent } from '../../../../components';
import { CalendarComponent } from '../../../../components/calendar/calendar.component';
import { RecurrenceScheduleComponent } from '../../../../components/recurrence-schedule/recurrence-schedule.component';
import { LockInitialWidthDirective } from '../../../../directives/lock-initial-width.directive';
import { AppInsightsService } from '../../../../services/app-insights.service';
import {
  IOrderItemAddRequest,
  IOrderItemInstanceAddRequest,
  IOrderItemInstanceUpdateRequest,
  IOrderItemType,
  IOrderItemUpdateRequest,
  IRecurrenceSchedule
} from '../../../../services/insecta';
import { OrderItemInstanceService } from '../../../../services/insecta/order-item-instance.service';
import { OrderItemTypeService } from '../../../../services/insecta/order-item-type.service';
import { OrderItemService } from '../../../../services/insecta/orderitem.service';
import { ProfileType } from '../../../../services/msgraph.service';
import { ContainerInfestationRateComponent } from '../container-infestation-rate/container-infestation-rate.component';
import { InsectItemComponent } from '../insect-item/insect-item.component';

@Component({
  selector: 'app-order-insect',
  standalone: true,
  imports: [
    InsectItemComponent,
    ContainerInfestationRateComponent,
    FloatLabelModule,
    DatePickerModule,
    CommonModule,
    FormsModule,
    InputTextModule,
    ToggleSwitchModule,
    TextareaModule,
    SelectModule,
    TreeSelectModule,
    ButtonModule,
    ProcessingComponent,
    DialogModule,
    RecurrenceScheduleComponent,
    CalendarComponent,
    TooltipModule,
    LockInitialWidthDirective
  ],
  templateUrl: './order-insect.component.html'
})
export class OrderInsectComponent implements OnInit {
  private readonly orderItemService = inject(OrderItemService);
  private readonly orderItemInstanceService = inject(OrderItemInstanceService);
  private readonly msgService = inject(MessageService);
  private readonly orderItemTypeService = inject(OrderItemTypeService);
  private readonly appInsights = inject(AppInsightsService);

  @ViewChild('calendarComp') calendarComp!: CalendarComponent;
  @ViewChild('recurrenceComp') recurrenceComp!: RecurrenceScheduleComponent;
  @ViewChild('projectSelect', { read: ElementRef }) projectSelect!: ElementRef;

  @Input() orderId!: string;
  @Input() catalogId!: string;
  @Input() locationId!: string;
  @Input() projects!: any[];
  @Input() catalogInformation!: any[];
  @Input() profile!: ProfileType;
  @Input() editingOrderItemDetails: any;
  @Input() editing: boolean = false;
  @Input() editingInstance: boolean = false;
  @Input() editingMode!: string;
  @Input() selectedOrderInstance: any;
  @Input() canApprove: boolean = false;
  @Input() status!: string;
  @Input() modalTitle: string = '';
  @Input() projectTooltipText: string = '';
  @Output() public dialogCloseEvent = new EventEmitter();
  @Output() public dialogCancelEvent = new EventEmitter();
  @Input() locationNodes: TreeNode[] = [];
  selectedLocationNode: TreeNode | null = null;

  orderInsect: {
    deliveryDate: Date | null;
    note: any;
    projectId: any;
    insectItem: any;
    containerItem: any;
    recurring: boolean;
    deliveryLocationId?: string | null;
  } = {
      deliveryDate: null,
      note: null,
      projectId: null,
      insectItem: {},
      containerItem: {},
      recurring: false,
     deliveryLocationId: '00000000-0000-0000-0000-000000000000'
    };

  editingOrderItemInsectVariant: {
    catalogInsectVariantId: string | null;
    catalogContainerInsectGrowthStageId: string | null;
    insectPermitId: string | null;
    insectQuantity: number | null;
  } = {
      catalogInsectVariantId: null,
      catalogContainerInsectGrowthStageId: null,
      insectPermitId: null,
      insectQuantity: null
    };

  editingOrderItemContainerInfestation: {
    catalogContainerInsectGrowthStageId: string | null;
    infestationRateQuantity: number | null;
    standardInfestationRate: boolean | null;
    numberOfContainers: number | null;
  } = {
      catalogContainerInsectGrowthStageId: null,
      infestationRateQuantity: null,
      standardInfestationRate: null,
      numberOfContainers: null
    };

  minDate: Date = new Date();
  maxDate: Date = new Date();
  isProcessing = false;
  unavailableDates: Date[] = [];
  unavailableDatesIsoStrings: string[] = [];
  invalidDays: number[] = [];
  recurringDates: string[] = [];
  invalidRecurringDates: string[] = [];
  insectOrderItemType: any = {} as IOrderItemType;
  selectedCatalogInsectGrowthStageId: string | null = null;
  selectedCatalogInsectVariantId: string | null = null;
  containerDisabled: boolean = true;
  showRecurringDialog = false;
  selectedDates: string[] = [];
  firstDeliveryDate: Date | null = null;
  insectGrowthStageSelected: boolean = false;
  recurrenceSchedule: IRecurrenceSchedule | undefined = undefined;
  currentInsectGrowthStageId: string | null = null;
  currentInsectVariantId: string | null = null;
  insectInfestationReset: boolean = false;
  showAddButton = true;
  selectedEditingDetailsOrderInstance: any;
  leadTime: any;
  defaultRecurrenceSchedule: IRecurrenceSchedule | undefined = undefined;
  defaultSelectedDates: string[] = [];
  defaultFirstDeliveryDate: Date | null = null;
  subsetSelectedDates: string[] = [];
  subsetInstances: any[] = [];
  rescheduleInstanceDates: string[] = [];
  showMakeRecurringButton: boolean = false;
  showRecurringDatesTooltip: boolean = false;
  isInsectFormValid: boolean = false;
  numberOfContainers: number | null = null;

  private findNodeByKey(nodes: TreeNode[], key: string): TreeNode | null {
    for (const node of nodes) {
      if (isStringEqual(node.key!, key)) {
        return node;
      }
      if (node.children && node.children.length > 0) {
        const found = this.findNodeByKey(node.children, key);
        if (found) return found;
      }
    }
    return null;
  }

  onLocationNodeSelect(event: any): void {
    this.orderInsect.deliveryLocationId = event.node?.key || null;
  }

  ngOnInit(): void {
    this.isProcessing = true;
    this.insectOrderItemType = this.catalogInformation[0].OrderItemTypes.find((item: any) => item.OrderItemTypeName === 'Insect');
    console.log(this.editing, this.editingInstance, this.editingMode, this.status);
    if (this.editing) {
      if (this.editingInstance) {
        if (isStringEqual(this.editingMode, 'single')) {
          this.showMakeRecurringButton = false;
        } else {
          if (isStringEqual(this.status, 'Approved') || isStringEqual(this.status, 'Updated')) {
            this.showMakeRecurringButton = true;
          }
        }
      } else {
        this.showMakeRecurringButton = true;
      }

      this.selectedEditingDetailsOrderInstance = this.editingInstance
        ? this.editingOrderItemDetails.orderItemInstances.find((inst: any) => isStringEqual(inst.id, this.selectedOrderInstance.id))
        : null;
      this.showAddButton = false;
      this.orderInsect.note = this.editingInstance ? this.selectedEditingDetailsOrderInstance.note : this.editingOrderItemDetails.note;
      this.orderInsect.projectId = this.editingOrderItemDetails.projectId?.toUpperCase();
      this.orderInsect.deliveryLocationId = this.editingInstance ? this.selectedEditingDetailsOrderInstance.deliveryLocationId : this.editingOrderItemDetails.deliveryLocationId;
      this.selectedLocationNode = this.findNodeByKey(this.locationNodes, this.orderInsect.deliveryLocationId!) || null;

      this.editingOrderItemInsectVariant = {
        catalogInsectVariantId: this.editingOrderItemDetails.catalogInsectVariantId,
        catalogContainerInsectGrowthStageId: this.editingOrderItemDetails.catalogContainerInsectGrowthStageId,
        insectPermitId: this.editingInstance ? this.selectedEditingDetailsOrderInstance.insectPermitId : this.editingOrderItemDetails.insectPermitId,
        insectQuantity: this.editingInstance ? this.selectedEditingDetailsOrderInstance.insectQuantity : this.editingOrderItemDetails.insectQuantity
      };

    } else {
      this.orderInsect.deliveryLocationId = this.locationId;
      this.selectedLocationNode = this.findNodeByKey(this.locationNodes, this.locationId) || null;
      this.showMakeRecurringButton = true;
      this.isProcessing = false;
    }
  }

  onReceiveInsectData(event: any): void {
    if (isEmpty(event.insectGrowthStageId) || isEmpty(event.insectVariantId)) {
      this.insectInfestationReset = true;
      this.orderInsect.deliveryDate = null;
      this.orderInsect.insectItem = {};
      this.orderInsect.containerItem = {};
      this.orderInsect.recurring = false;
      this.insectGrowthStageSelected = false;
      this.containerDisabled = true;
      this.removeRecurrences();
      this.computeInsectValidation(false);
      return;
    }

    this.selectedCatalogInsectGrowthStageId = event.catalogInsectGrowthStageId;

    this.containerDisabled = false;
    const newGSId = event.insectGrowthStageId;
    const newVarId = event.insectVariantId;

    if (this.currentInsectGrowthStageId === newGSId && this.currentInsectVariantId === newVarId) {
      this.orderInsect.insectItem = cloneDeep(event);
      this.calculateNumberOfContainers();
      this.computeInsectValidation(false);
    } else {
      this.isProcessing = true;
      this.orderInsect.insectItem = cloneDeep(event);
      this.currentInsectGrowthStageId = this.orderInsect.insectItem.insectGrowthStageId;
      this.currentInsectVariantId = this.orderInsect.insectItem.insectVariantId;

      this.insectGrowthStageSelected = false;
      this.insectInfestationReset = true;
      this.orderInsect.deliveryDate = null;
      this.orderInsect.containerItem = {};
      this.orderInsect.recurring = false;
      this.removeRecurrences();

      if (this.editing) {
        this.editingOrderItemContainerInfestation = {
          catalogContainerInsectGrowthStageId: this.editingOrderItemDetails.catalogContainerInsectGrowthStageId,
          infestationRateQuantity: this.editingInstance
            ? this.selectedEditingDetailsOrderInstance.infestationRateQuantity
            : this.editingOrderItemDetails.infestationRateQuantity,
          standardInfestationRate: this.editingInstance
            ? this.selectedEditingDetailsOrderInstance.standardInfestationRate
            : this.editingOrderItemDetails.standardInfestationRate,
          numberOfContainers: this.editingInstance
            ? this.selectedEditingDetailsOrderInstance.numberOfContainers
            : this.editingOrderItemDetails.numberOfContainers
        };
      }

      firstValueFrom(
        this.orderItemTypeService.getRecurringOrderItemTypesConstraints(
          this.catalogId,
          this.insectOrderItemType.OrderItemTypeId,
          this.orderInsect.insectItem.insectVariantId,
          this.orderInsect.insectItem.insectGrowthStageId,
          null
        )
      )
        .then((data) => {
          this.leadTime = data.maxLeadTime;
          const DEFAULT_MIN_DATE = parseLocalDate('2000-01-01');
          const DEFAULT_MAX_DATE = new Date(2100, 11, 31);
          this.minDate = this.canApprove ? DEFAULT_MIN_DATE : data.minDate === null ? DEFAULT_MIN_DATE : parseLocalDate(data.minDate);
          this.maxDate = data.maxDate === null ? DEFAULT_MAX_DATE : parseLocalDate(data.maxDate);
          this.invalidDays = [0, 1, 2, 3, 4, 5, 6].filter((day) => !data.availableDays.includes(day));
          this.unavailableDates = data.unavailableDates.map(parseLocalDate);
          this.unavailableDatesIsoStrings = this.unavailableDates.map((date) => {
            const yyyy = date.getFullYear();
            const mm = String(date.getMonth() + 1).padStart(2, '0');
            const dd = String(date.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
          });
        })
        .catch((err) => {
          notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'OrderInsectComponent', action: 'LoadRecurringConstraints' });
        })
        .finally(() => {
          if (this.editing) {
            let firstDeliveryDate: Date | null = null;
            if (this.editingInstance) {
              if (this.selectedEditingDetailsOrderInstance && this.selectedEditingDetailsOrderInstance.deliveryDate) {
                firstDeliveryDate = parseLocalDate(this.selectedEditingDetailsOrderInstance.deliveryDate);
              }
            } else {
              if (this.editingOrderItemDetails && this.editingOrderItemDetails.firstDeliveryDate) {
                firstDeliveryDate = parseLocalDate(this.editingOrderItemDetails.firstDeliveryDate);
              }
            }
            if (firstDeliveryDate && firstDeliveryDate < this.minDate && !this.canApprove) {
              warningMsg(this.msgService, 'The first delivery date for this Order Item is no longer valid and must be reselected');
              this.orderInsect.deliveryDate = null;
              this.selectedDates = [];
              this.recurrenceSchedule = undefined;
            } else {
              this.orderInsect.deliveryDate = firstDeliveryDate;
              if (
                (this.editingOrderItemDetails && this.editingOrderItemDetails.orderItemInstances) ||
                (this.editingInstance && this.editingMode === 'future' && this.status === 'Updated')
              ) {
                this.subsetSelectedDates = this.getSubsetSelectedDates();
                this.selectedDates = this.editingInstance
                  ? this.subsetSelectedDates
                  : this.editingOrderItemDetails.orderItemInstances.map((inst: { deliveryDate: any }) => inst.deliveryDate);
                this.recurrenceSchedule = this.editingOrderItemDetails.recurrenceSchedule;
                this.updateShowRecurringDatesTooltip();
              }
            }
            this.editing = false;
          } else {
            this.orderInsect.deliveryDate = null;
          }
          this.insectGrowthStageSelected = true;
          this.isProcessing = false;
          this.computeInsectValidation(false);
        });
    }
  }

  getSubsetSelectedDates(): string[] {
    if (!this.editingOrderItemDetails || !this.selectedEditingDetailsOrderInstance) {
      return [];
    }

    const selectedSeq = this.selectedEditingDetailsOrderInstance.orderItemInstanceSequence;
    this.subsetInstances = this.editingOrderItemDetails.orderItemInstances.filter((inst: any) => inst.orderItemInstanceSequence >= selectedSeq);
    return this.subsetInstances.map((inst: any) => inst.deliveryDate);
  }

  onDeliveryDateChange() {
    if (!this.editingInstance && this.selectedDates.length < 2) {
      this.selectedDates = this.orderInsect.deliveryDate === null ? [] : [dateToIsoString(this.orderInsect.deliveryDate)];
    }
    this.computeInsectValidation(false);
  }

  onReceiveContainerData(event: any): void {
    this.orderInsect.containerItem = cloneDeep(event);
    this.calculateNumberOfContainers();
    this.computeInsectValidation(false);
    if (this.insectInfestationReset) {
      this.insectInfestationReset = false;
    }
  }

  private calculateNumberOfContainers(): void {
    const insectQuantity = this.orderInsect.insectItem?.quantity;
    const infestationRate = this.orderInsect.containerItem?.quantityPerContainer;

    if (insectQuantity != null && insectQuantity > 0 && infestationRate != null && infestationRate > 0) {
      const numContainers = Math.ceil(insectQuantity / infestationRate);
      this.orderInsect.containerItem.numberOfContainers = numContainers;
      this.numberOfContainers = numContainers;
    } else {
      this.orderInsect.containerItem.numberOfContainers = null;
      this.numberOfContainers = null;
    }
  }

  onClose() {
    if (this.editing) {
      this.editing = false;
      this.editingInstance = false;
      this.editingMode = '';
    }
    this.dialogCloseEvent.emit(false);
  }

  onCancel() {
    if (this.editing) {
      this.editing = false;
      this.editingInstance = false;
      this.editingMode = '';
    }
    this.dialogCancelEvent.emit(false);
  }

  openMakeRecurringDialog() {
    if (!this.orderInsect.insectItem?.insectGrowthStageId || !this.orderInsect.insectItem?.insectVariantId) {
      warningMsg(this.msgService, 'Please select an Insect Variant and Growth Stage first');
      return;
    }
    this.firstDeliveryDate = this.orderInsect.deliveryDate ? new Date(this.orderInsect.deliveryDate) : null;
    this.defaultRecurrenceSchedule = cloneDeep(this.recurrenceSchedule);
    this.defaultSelectedDates = cloneDeep(this.selectedDates);
    this.defaultFirstDeliveryDate = cloneDeep(this.firstDeliveryDate);
    this.showRecurringDialog = true;
  }

  onSaveOrderItem() {
    if (!this.computeInsectValidation(true)) {
      return;
    }

    this.isProcessing = true;
    const orderItem: IOrderItemAddRequest = {
      orderId: this.orderId,
      orderItemTypeId: this.insectOrderItemType.OrderItemTypeId,
      projectId: this.orderInsect.projectId || null,
      firstDeliveryDate: this.orderInsect.deliveryDate === null ? '' : dateToIsoString(this.orderInsect.deliveryDate),
      note: this.orderInsect.note || null,
      actionBy: this.profile.userPrincipalName,
      catalogInsectVariantId: this.orderInsect.insectItem.catalogInsectVariantId,
      catalogContainerInsectGrowthStageId: this.orderInsect.containerItem.catalogContainerInsectGrowthStageId,
      insectQuantity: this.orderInsect.insectItem.quantity,
      insectPermitId: this.orderInsect.insectItem.permitId,
      infestationRateQuantity: this.orderInsect.containerItem.quantityPerContainer,
      catalogContainerSubstrateId: undefined,
      substrateAmount: undefined,
      substrateAmountUom: undefined,
      substrateQuantity: this.orderInsect.containerItem.numberOfContainers,
      recurrenceSchedule: this.recurrenceSchedule,
      standardFillAmount: false,
      standardInfestationRate: this.orderInsect.containerItem.standard,
      instances: this.buildInstanceObject()
    };
    firstValueFrom(this.orderItemService.addOrderItem(this.orderId, orderItem))
      .then(() => {
        successMsg(this.msgService, 'Order item saved successfully');
        this.onClose();
      })
      .catch((err) => {
        this.isProcessing = false;
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'OrderInsectComponent', action: 'AddOrderItem' });
      })
      .finally(() => {
        this.isProcessing = false;
      });
  }

  onEditSaveOrderItem() {
    if (!this.computeInsectValidation(true)) {
      return;
    }

    this.isProcessing = true;
    if (!this.editingInstance) {
      const orderItem: IOrderItemUpdateRequest = {
        id: this.editingOrderItemDetails.id,
        orderItemTypeId: this.editingOrderItemDetails.orderItemTypeId,
        projectId: this.orderInsect.projectId || null,
        firstDeliveryDate: this.orderInsect.deliveryDate === null ? '' : dateToIsoString(this.orderInsect.deliveryDate),
        note: this.orderInsect.note || null,
        actionBy: this.profile.userPrincipalName,
        catalogInsectVariantId: this.orderInsect.insectItem.catalogInsectVariantId,
        catalogContainerInsectGrowthStageId: this.orderInsect.containerItem.catalogContainerInsectGrowthStageId,
        insectQuantity: this.orderInsect.insectItem.quantity,
        insectPermitId: this.orderInsect.insectItem.permitId,
        infestationRateQuantity: this.orderInsect.containerItem.quantityPerContainer,
        catalogContainerSubstrateId: undefined,
        substrateAmount: undefined,
        substrateAmountUom: undefined,
        substrateQuantity: this.orderInsect.containerItem.numberOfContainers,
        recurrenceSchedule: this.recurrenceSchedule,
        standardFillAmount: false,
        standardInfestationRate: this.orderInsect.containerItem.standardInfestationRate,
        deliveryLocationId: this.orderInsect.deliveryLocationId || null,
        instances: this.buildInstanceObject()
      };
      firstValueFrom(this.orderItemService.updateOrderItem(this.editingOrderItemDetails.orderId, orderItem))
        .then(() => {
          successMsg(this.msgService, 'Order item saved successfully');
          this.onClose();
        })
        .catch((err) => {
          this.isProcessing = false;
          notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'OrderInsectComponent', action: 'UpdateOrderItem' });
        })
        .finally(() => {
          this.isProcessing = false;
          this.editing = false;
        });
    }

    if (this.editingInstance) {
      this.rescheduleInstanceDates = this.selectedDates && this.selectedDates.length > 0 ? this.selectedDates : [];
      const orderItemInstance: IOrderItemInstanceUpdateRequest = {
        id: this.selectedOrderInstance.id,
        orderItemInstanceSequence: (() => {
          const seq = this.selectedOrderInstance.orderItemInstanceSequence;
          if (typeof seq === 'string') {
            const parts = seq.split('-');
            const last = parts[parts.length - 1];
            // Remove leading zeros and convert to number
            return Number(last.replace(/^0+/, ''));
          }
          return seq;
        })(),
        orderId: this.selectedOrderInstance.orderId,
        orderItemId: this.selectedOrderInstance.orderItemId,
        deliveryDate: this.orderInsect.deliveryDate === null ? '' : dateToIsoString(this.orderInsect.deliveryDate),
        note: this.orderInsect.note || null,
        catalogInsectVariantId: this.orderInsect.insectItem.catalogInsectVariantId,
        catalogContainerInsectGrowthStageId: this.orderInsect.containerItem.catalogContainerInsectGrowthStageId,
        insectQuantity: this.orderInsect.insectItem.quantity,
        insectPermitId: this.orderInsect.insectItem.permitId,
        infestationRateQuantity: this.orderInsect.containerItem.quantityPerContainer,
        catalogContainerSubstrateId: undefined,
        substrateAmount: undefined,
        substrateAmountUom: undefined,
        substrateQuantity: this.orderInsect.containerItem.numberOfContainers,
        standardFillAmount: false,
        standardInfestationRate: this.orderInsect.containerItem.standard,
        lastEditableDate: (() => {
          if (!this.orderInsect.deliveryDate || !this.leadTime) return undefined;
          const date = new Date(this.orderInsect.deliveryDate);
          date.setDate(date.getDate() - (this.leadTime || 0));
          return dateToIsoString(date);
        })(),
        deliveryLocationId: this.orderInsect.deliveryLocationId || null,
      };

      firstValueFrom(
        this.orderItemInstanceService.updateOrderItemInstance(
          this.editingOrderItemDetails.orderId,
          this.selectedOrderInstance.orderItemId,
          this.selectedOrderInstance.id,
          orderItemInstance,
          this.editingMode,
          this.status,
          this.rescheduleInstanceDates
        )
      )
        .then(() => {
          successMsg(this.msgService, 'Order instance saved successfully');
          this.onClose();
        })
        .catch((err) => {
          this.isProcessing = false;
          notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'OrderInsectComponent', action: 'UpdateOrderItemInstance' });
        })
        .finally(() => {
          this.isProcessing = false;
          this.editing = false;
          this.editingInstance = false;
          this.editingMode = '';
        });
    }
  }

  buildInstanceObject(): IOrderItemInstanceAddRequest[] {
    const datesToUse =
      this.selectedDates && this.selectedDates.length > 0
        ? this.selectedDates
        : this.orderInsect.deliveryDate === null
          ? null
          : [dateToIsoString(this.orderInsect.deliveryDate)];

    if (datesToUse === null || datesToUse.length === 0) {
      return [];
    }

    return datesToUse.map((date) => {
      const deliveryDateForCalc = new Date(date);
      const lastEditableDate = new Date(deliveryDateForCalc);
      lastEditableDate.setDate(lastEditableDate.getDate() - (this.leadTime || 0));

      return {
        deliveryDate: date,
        lastEditableDate: dateToIsoString(lastEditableDate),
        note: this.orderInsect.note || undefined,
        catalogInsectVariantId: this.orderInsect.insectItem.catalogInsectVariantId || undefined,
        catalogContainerInsectGrowthStageId: this.orderInsect.containerItem.catalogContainerInsectGrowthStageId || undefined,
        insectQuantity: this.orderInsect.insectItem.quantity || undefined,
        insectPermitId: this.orderInsect.insectItem.permitId || undefined,
        infestationRateQuantity: this.orderInsect.containerItem.quantityPerContainer || undefined,
        catalogContainerSubstrateId: undefined,
        substrateAmount: undefined,
        substrateAmountUom: undefined,
        substrateQuantity: this.orderInsect.containerItem.numberOfContainers || undefined,
        standardFillAmount: false,
        standardInfestationRate: this.orderInsect.containerItem.standard,
        vendorDelay: false,
        deliveryLocationId: this.orderInsect.deliveryLocationId || null
      };
    });
  }

  private computeInsectValidation(showToast: boolean): boolean {
    const insectItem = this.orderInsect.insectItem || {};
    const containerItem = this.orderInsect.containerItem || {};

    const insectGroupValid =
      (insectItem.quantity ?? 0) > 0 &&
      !isStringEmpty(insectItem.insectVariantId) &&
      !isStringEmpty(insectItem.catalogInsectGrowthStageId) &&
      !isStringEmpty(insectItem.permitId);

    const containerGroupValid = !isStringEmpty(containerItem.catalogContainerInsectGrowthStageId) && !isEmpty(containerItem.standard);

    const deliveryValid = !isStringEmpty(this.orderInsect.deliveryDate);

    const overallValid = insectGroupValid && containerGroupValid && deliveryValid;

    // Emit validation messages ONLY for the first failing group when showToast is true
    if (showToast && !overallValid) {
      let failingRules: { valid: boolean; message: string }[] = [];
      if (!insectGroupValid) {
        failingRules = [
          { valid: (insectItem.quantity ?? 0) > 0, message: 'Insect quantity must be greater than zero.' },
          { valid: !isStringEmpty(insectItem.insectVariantId), message: 'Insect variant is required.' },
          { valid: !isStringEmpty(insectItem.catalogInsectGrowthStageId), message: 'Insect growth stage is required.' },
          { valid: !isStringEmpty(insectItem.permitId), message: 'Permit is required.' }
        ];
      } else if (!containerGroupValid) {
        failingRules = [
          { valid: !isStringEmpty(containerItem.catalogContainerInsectGrowthStageId), message: 'Container growth stage selection is required.' },
          { valid: !isStringEmpty(containerItem.quantityPerContainer), message: 'Quantity per container is required.' },
          { valid: !isEmpty(containerItem.standard), message: 'Specify whether standard infestation rate is used.' },
          { valid: !isStringEmpty(containerItem.numberOfContainers), message: 'Number of containers is required.' }
        ];
      } else if (!deliveryValid) {
        failingRules = [{ valid: deliveryValid, message: 'Delivery date is required.' }];
      }
      // Use aggregateValidation for deduping and consistent toast emission
      aggregateValidation(this.msgService, failingRules, { prefix: VALIDATION_PREFIX, dedupe: true, silent: false });
    }

    this.isInsectFormValid = overallValid;
    return overallValid;
  }

  onSaveRecurrence(event: any) {
    this.selectedDates = event.recurrenceDates;
    this.invalidRecurringDates = event.invalidRecurrenceDates;
  }

  updateRecurrences(event: any) {
    this.selectedDates = event.recurrenceDates;
    this.invalidRecurringDates = event.invalidRecurrenceDates;
    if (event.recurrenceSchedule) {
      this.recurrenceSchedule = event.recurrenceSchedule;
    }

    if (this.selectedDates && this.selectedDates.length > 0) {
      this.orderInsect.deliveryDate = parseLocalDate(this.selectedDates[0]);
      if (this.recurrenceSchedule && this.recurrenceSchedule.configuration) {
        this.recurrenceSchedule.configuration.customDates = this.selectedDates;
      }
    }
    this.updateShowRecurringDatesTooltip();
  }

  addRecurrences() {
    const instanceCount = this.subsetInstances.length;
    const dateCount = this.selectedDates.length;

    if (instanceCount !== dateCount && this.editingInstance && (isStringEqual(this.status, 'Updated') || isStringEqual(this.status, 'Approved'))) {
      errorMsg(
        this.msgService,
        `The number of instances to edit is ${instanceCount}, but you have selected ${dateCount} dates. The number of selected dates must match the number of instances you are editing.`,
        true
      );
    } else if (this.invalidRecurringDates.length > 0) {
      errorMsg(this.msgService, 'Invalid recurrence dates found - please correct them before continuing');
    } else {
      this.showRecurringDialog = false;
      this.updateShowRecurringDatesTooltip();
    }
    this.computeInsectValidation(false);
  }

  removeRecurrences() {
    this.showRecurringDialog = false;
    this.recurrenceComp?.reset();
    this.selectedDates = [];
    this.invalidRecurringDates = [];
    this.firstDeliveryDate = cloneDeep(this.defaultFirstDeliveryDate);
    this.orderInsect.deliveryDate = cloneDeep(this.defaultFirstDeliveryDate);
    this.updateShowRecurringDatesTooltip();
    this.computeInsectValidation(false);
  }

  cancelRecurrences() {
    this.showRecurringDialog = false;
    this.recurrenceComp?.reset();
    this.selectedDates = cloneDeep(this.defaultSelectedDates);
    this.recurrenceSchedule = cloneDeep(this.defaultRecurrenceSchedule);
    this.firstDeliveryDate = cloneDeep(this.defaultFirstDeliveryDate);
    this.orderInsect.deliveryDate = cloneDeep(this.defaultFirstDeliveryDate);
    this.invalidRecurringDates = [];
    this.updateShowRecurringDatesTooltip();
    this.computeInsectValidation(false);
  }

  get recurringDatesTooltip(): string {
    return this.selectedDates && this.selectedDates.length > 0 ? 'Recurring Dates:\n' + this.selectedDates.join('\n') : '';
  }

  private updateShowRecurringDatesTooltip() {
    if (this.editingInstance) {
      if (isStringEqual(this.editingMode, 'single')) {
        this.showRecurringDatesTooltip = false;
      } else {
        if (isStringEqual(this.status, 'Approved') || isStringEqual(this.status, 'Updated')) {
          this.showRecurringDatesTooltip = (this.selectedDates && this.selectedDates.length > 1) || false;
        }
      }
    } else {
      this.showRecurringDatesTooltip = (this.selectedDates && this.selectedDates.length > 1) || false;
    }
  }

  onDialogShow() {
    afterRender(() => {
      if (this.calendarComp && this.calendarComp.calendarComponent) {
        this.calendarComp.calendarComponent.getApi().updateSize();
      }
    });
  }
}
